import { Alert, Anchor, Badge, Box, Breadcrumbs, Button, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core'
import { TbAlertTriangle, TbExternalLink } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useMemo } from 'react'

import {
  getAllRouteStatisticsOptions,
  getArchivedUnexpectedEventsQueryKey,
  getPendingUnexpectedEventsQueryKey,
  getUnexpectedEventDetailsOptions,
  getUnexpectedEventDetailsQueryKey,
  resolveUnexpectedEventMutation,
  unresolveUnexpectedEventMutation,
} from '@/api/main/@tanstack/react-query.gen'
import { UnexpectedEventDetail as UnexpectedEventDetailType, UnexpectedEventType } from '@/api/main/types.gen'
import { useAuthStore } from '@/stores/auth'
import { PermissionGuard } from '@/utils/permissions'
import { toPragueLocaleString } from '@/utils/datetime'
import { googleMapsLink } from '@/utils/googleMapsLink'

import CopyableId from './CopyableId'
import { computeSanitizedLocation } from './eventLocation'
import UnexpectedEventPhotos from './UnexpectedEventPhotos'
import DriveSegmentsMap from './DriveSegmentsMap'
import StaticNavigationMap from './StaticNavigationMap'
import MapyCzPanel from './MapyCzPanel'
import GoogleMapsPanel from './GoogleMapsPanel'
import AffectedRoutesPanel from './AffectedRoutesPanel'
import AnnexationsPanel from './AnnexationsPanel'
import { DISPLAY_ANNEXATIONS } from '@/constants'

const typeKey = (type: UnexpectedEventType): string => {
  switch (type) {
    case 'unknown':
      return 'unknown'
    case 'turn_not_possible':
      return 'turnNotPossible'
    case 'road_temporarily_blocked':
      return 'roadTemporarilyBlocked'
    case 'road_closed':
      return 'roadClosed'
    case 'inaccurate_map':
      return 'inaccurateMap'
    case 'inaccurate_gps':
      return 'inaccurateGps'
    default:
      return 'unknown'
  }
}

const PanelHeader = ({ children }: { children: React.ReactNode }) => (
  <Title order={5} mb="xs">
    {children}
  </Title>
)

const UnexpectedEventDetail = () => {
  const { t } = useTranslation('unexpectedEvents')
  const { eventId } = useParams({ from: '/unexpected-events/$eventId' })
  const listSearch = useSearch({ from: '/unexpected-events' })
  const { userPermissions } = useAuthStore()
  const queryClient = useQueryClient()

  const breadcrumbs = (
    <Breadcrumbs separator="/">
      <Anchor
        size="sm"
        renderRoot={({ className, ...others }) => (
          <Link to="/unexpected-events" search={listSearch} className={className} {...others} />
        )}
      >
        {t('title')}
      </Anchor>
      <Text size="sm" c="dimmed" ff="monospace" title={eventId}>
        {eventId.length > 10 ? `${eventId.slice(0, 8)}…` : eventId}
      </Text>
    </Breadcrumbs>
  )

  const detailQuery = useQuery(getUnexpectedEventDetailsOptions({ path: { event_id: eventId } }))

  const { data: routes, isError: isRoutesError } = useQuery(getAllRouteStatisticsOptions())

  const activeRouteVersion = routes?.find(
    ({ route_id, is_active }) => is_active && route_id === detailQuery.data?.route_id
  )?.route_version

  const details: UnexpectedEventDetailType | undefined = detailQuery.data

  const sanitizedLocation = useMemo(() => (details ? computeSanitizedLocation(details) : undefined), [details])

  const invalidateDetail = () => {
    queryClient.invalidateQueries({
      queryKey: getUnexpectedEventDetailsQueryKey({ path: { event_id: eventId } }),
    })
    queryClient.invalidateQueries({ queryKey: getPendingUnexpectedEventsQueryKey() })
    queryClient.invalidateQueries({ queryKey: getArchivedUnexpectedEventsQueryKey() })
  }

  const { mutate: resolveEvent, isPending: isResolving } = useMutation({
    ...resolveUnexpectedEventMutation(),
    onSuccess: () => {
      notifications.show({ message: t('header.resolveSuccess'), color: 'green' })
      invalidateDetail()
    },
    onError: () => {
      notifications.show({ message: t('header.resolveError'), color: 'red' })
    },
  })

  const { mutate: unresolveEvent, isPending: isUnresolving } = useMutation({
    ...unresolveUnexpectedEventMutation(),
    onSuccess: () => {
      notifications.show({ message: t('header.unresolveSuccess'), color: 'green' })
      invalidateDetail()
    },
    onError: () => {
      notifications.show({ message: t('header.unresolveError'), color: 'red' })
    },
  })

  if (detailQuery.isError || isRoutesError) {
    return (
      <Box p="md">
        <Stack gap="sm">
          {breadcrumbs}
          <Alert color="red" icon={<TbAlertTriangle />} title={t('loadDetailFailed')} />
        </Stack>
      </Box>
    )
  }

  if (!details) {
    return (
      <Box p="md" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader />
      </Box>
    )
  }

  const patrolVehicle = details.navigation_data?.patrol_vehicle
  const driverName = details.navigation_data?.driver?.name
  const navData = details.navigation_data
  const currentSegment = navData?.current_street_segment
  const futureSegments = navData?.future_street_segments ?? []
  const affectingSegmentIds = [
    currentSegment?.street_segment_id,
    futureSegments[0]?.street_segment_id,
    futureSegments[1]?.street_segment_id,
  ].filter((id): id is string => Boolean(id))

  const displayActiveRouteVersion = activeRouteVersion && activeRouteVersion !== details.route_version

  return (
    <Box
      p="md"
      style={{
        display: 'grid',
        gap: 'var(--mantine-spacing-md)',
        width: '100%',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gridAutoRows: 'min-content',
        minHeight: 'calc(100vh - 90px)',
      }}
    >
      <Paper p="md" withBorder style={{ gridColumn: '1 / -1' }}>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Group gap="md" wrap="wrap" align="center">
              {breadcrumbs}
              <Badge color={details.is_processed ? 'green' : 'orange'} variant="light">
                {details.is_processed ? t('header.resolved') : t(typeKey(details.type))}
              </Badge>
              <Text size="sm" c="dimmed">
                {toPragueLocaleString(new Date(details.observed_at))}
              </Text>
            </Group>

            <Group gap="xl" wrap="wrap" mt="xs">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  {t('header.eventId')}
                </Text>
                <CopyableId value={details.id} />
              </Stack>

              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  {t('header.driveId')}
                </Text>
                <Group gap={4}>
                  <Link
                    to="/drive-details/$id"
                    params={{ id: String(details.scheduled_drive_id) }}
                    style={{ color: 'var(--mantine-color-blue-6)', textDecoration: 'underline', fontSize: 14 }}
                  >
                    {details.scheduled_drive_id}
                  </Link>
                  <CopyableId value={String(details.scheduled_drive_id)} display={null} />
                </Group>
              </Stack>

              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  {t('header.routeId')}
                </Text>
                <Group gap={4}>
                  <Link
                    to="/route/$routeId/$version"
                    params={{ routeId: String(details.route_id), version: String(details.route_version) }}
                    style={{ color: 'var(--mantine-color-blue-6)', textDecoration: 'underline', fontSize: 14 }}
                  >
                    {details.route_id}:{details.route_version}
                  </Link>
                  <CopyableId value={`${details.route_id}:${details.route_version}`} display={null} />
                </Group>
              </Stack>

              {displayActiveRouteVersion && (
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    {t('header.activeRouteVersion')}
                  </Text>
                  <Group gap={4}>
                    <Link
                      to="/route/$routeId/$version"
                      params={{ routeId: String(details.route_id), version: String(activeRouteVersion) }}
                      style={{ color: 'var(--mantine-color-blue-6)', textDecoration: 'underline', fontSize: 14 }}
                    >
                      {activeRouteVersion}
                    </Link>
                    <CopyableId value={String(activeRouteVersion)} display={null} />
                  </Group>
                </Stack>
              )}

              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  {t('header.driver')}
                </Text>
                <Text size="sm">{driverName ?? t('header.noDriver')}</Text>
              </Stack>

              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  {t('header.gps')}
                </Text>
                {patrolVehicle?.position?.latitude !== undefined && patrolVehicle?.position?.longitude !== undefined ? (
                  <Anchor
                    href={googleMapsLink(patrolVehicle.position.latitude!, patrolVehicle.position.longitude!)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                  >
                    <Group gap={4} wrap="nowrap">
                      <span>
                        {patrolVehicle.position.latitude!.toFixed(6)}, {patrolVehicle.position.longitude!.toFixed(6)}
                      </span>
                      <TbExternalLink size={12} />
                    </Group>
                  </Anchor>
                ) : (
                  <Text size="sm" c="dimmed">
                    {t('header.noGps')}
                  </Text>
                )}
              </Stack>
            </Group>
          </Stack>

          <PermissionGuard userPermissions={userPermissions} resource="unexpected_events" action="write">
            {details.is_processed ? (
              <Button
                color="blue"
                loading={isUnresolving}
                onClick={() =>
                  modals.openConfirmModal({
                    title: t('confirm.unresolveTitle'),
                    centered: true,
                    size: 'xl',
                    children: (
                      <Stack gap="xs">
                        <Text size="sm">{t('confirm.unresolveBody')}</Text>
                        <Group gap="xs" align="center" wrap="nowrap">
                          <Badge color="green" variant="light">
                            {t('header.resolved')}
                          </Badge>
                          <Text size="sm" c="dimmed">
                            {toPragueLocaleString(new Date(details.observed_at))}
                          </Text>
                        </Group>
                        <CopyableId value={details.id} />
                      </Stack>
                    ),
                    labels: { confirm: t('confirm.confirm'), cancel: t('confirm.cancel') },
                    confirmProps: { color: 'blue' },
                    onConfirm: () => unresolveEvent({ path: { event_id: details.id } }),
                  })
                }
              >
                {t('header.unresolve')}
              </Button>
            ) : (
              <Button
                color="blue"
                loading={isResolving}
                onClick={() =>
                  modals.openConfirmModal({
                    title: t('confirm.resolveTitle'),
                    centered: true,
                    size: 'xl',
                    children: (
                      <Stack gap="xs">
                        <Text size="sm">{t('confirm.resolveBody')}</Text>
                        <Group gap="xs" align="center" wrap="nowrap">
                          <Badge color={'orange'} variant="light">
                            {t(typeKey(details.type))}
                          </Badge>
                          <Text size="sm" c="dimmed">
                            {toPragueLocaleString(new Date(details.observed_at))}
                          </Text>
                        </Group>
                        <CopyableId value={details.id} />
                      </Stack>
                    ),
                    labels: { confirm: t('confirm.confirm'), cancel: t('confirm.cancel') },
                    confirmProps: { color: 'blue' },
                    onConfirm: () => resolveEvent({ path: { event_id: details.id } }),
                  })
                }
              >
                {t('header.resolve')}
              </Button>
            )}
          </PermissionGuard>
        </Group>
      </Paper>

      <Paper p="md" withBorder style={{ gridColumn: '1 / 3', minHeight: 480 }}>
        <PanelHeader>{t('panels.photos')}</PanelHeader>
        <UnexpectedEventPhotos eventId={eventId} />
      </Paper>

      <Paper p="md" withBorder style={{ gridColumn: '3', minHeight: 480 }}>
        <PanelHeader>{t('panels.drivenSkippedSegments')}</PanelHeader>
        <DriveSegmentsMap
          patrolVehicle={patrolVehicle}
          drivenSegments={details.driven_segments}
          skippedSegments={details.skipped_segments}
          segmentsGeojson={details.segments_geojson as Record<string, unknown>}
          mapPosition={sanitizedLocation}
          height={400}
          zoom={17}
        />
      </Paper>

      <Paper p="md" withBorder style={{ gridColumn: '1', minHeight: 520 }}>
        <PanelHeader>{t('panels.navigation')}</PanelHeader>
        <StaticNavigationMap
          id={`unexpectedEventMap-${eventId}`}
          navigationData={navData}
          patrolVehicle={patrolVehicle}
          position={sanitizedLocation}
          initialZoom={17}
          height={360}
        />
        <Box mt="sm">
          <Text fw={600} size="sm" mb={4}>
            {t('panels.lastSegmentIds')}
          </Text>
          <Stack gap={4}>
            {currentSegment && (
              <SegmentIdRow color="var(--mantine-color-violet-5)" id={currentSegment.street_segment_id} />
            )}
            {futureSegments[0] && (
              <SegmentIdRow color="var(--mantine-color-red-6)" id={futureSegments[0].street_segment_id} />
            )}
            {futureSegments[1] && (
              <SegmentIdRow color="var(--mantine-color-yellow-6)" id={futureSegments[1].street_segment_id} />
            )}
          </Stack>
        </Box>
      </Paper>

      <Paper p="md" withBorder style={{ gridColumn: '2', minHeight: 520 }}>
        <MapyCzPanel location={sanitizedLocation} height={360} />
        <Box mt="sm">
          <PanelHeader>{t('panels.affectedRoutes')}</PanelHeader>
          <AffectedRoutesPanel segmentIds={affectingSegmentIds} />
        </Box>
      </Paper>

      <Paper p="md" withBorder style={{ gridColumn: '3', minHeight: 520 }}>
        {DISPLAY_ANNEXATIONS && (
          <Box mb="sm">
            <PanelHeader>{t('panels.annexations')}</PanelHeader>
            <AnnexationsPanel location={sanitizedLocation} height={300} />
          </Box>
        )}
        <GoogleMapsPanel location={sanitizedLocation} height={360} />
      </Paper>
    </Box>
  )
}

const SegmentIdRow = ({ color, id }: { color: string; id: string }) => (
  <Group gap="xs" wrap="nowrap" align="center">
    <Box w={14} h={14} style={{ borderRadius: '50%', background: color }} />
    <CopyableId value={id} />
  </Group>
)

export default UnexpectedEventDetail
